import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Circle, CheckCircle2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface SortableTaskCardProps {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  onDelete?: () => void;
  onToggleComplete?: () => void;
}

export function SortableTaskCard({
  id,
  title,
  description,
  status,
  onDelete,
  onToggleComplete,
}: SortableTaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isCompleted = status === "done";

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "group flex items-start gap-3 rounded-lg bg-card dark:bg-slate-800/80 border border-border/50 p-3 cursor-grab active:cursor-grabbing touch-none transition-all duration-200",
        "hover:border-border hover:shadow-sm",
        isDragging && "opacity-60 shadow-xl z-50 rotate-1 scale-105 ring-2 ring-primary ring-offset-2"
      )}
    >
      {/* Checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onToggleComplete?.();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className={cn(
          "flex-shrink-0 mt-0.5 transition-colors",
          isCompleted ? "text-emerald-500" : "text-muted-foreground hover:text-foreground"
        )}
      >
        {isCompleted ? (
          <CheckCircle2 className="h-5 w-5" />
        ) : (
          <Circle className="h-5 w-5" />
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-sm leading-snug",
          isCompleted && "line-through text-muted-foreground"
        )}>
          {title}
        </p>
        {description && !isCompleted && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {description}
          </p>
        )}
      </div>

      {/* Delete */}
      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
